import React from 'react'
import { Button } from '../../ui/button'
import { ArrowLeft, Copy, ExternalLink, Code } from 'lucide-react'
import { format } from 'date-fns'
import toast from 'react-hot-toast'
import { Link } from 'react-router-dom'

interface KycHeaderProps {
  transactionId: string
  artifactId: string
  insertedAt: string
  onBack?: () => void
  onViewRawJson?: () => void
}

export const KycHeader: React.FC<KycHeaderProps> = ({
  transactionId,
  artifactId,
  insertedAt,
  onBack,
  onViewRawJson
}) => {
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copied to clipboard`)
    }).catch(() => {
      toast.error('Failed to copy to clipboard')
    })
  }

  return (
    <div className="space-y-2">
      {/* Navigation */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          )}
          <h2 className="text-2xl font-bold tracking-tight dark:text-white">
            KYC Verification
          </h2>
        </div>
        
        <div className="flex items-center space-x-2">
          {onViewRawJson && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={onViewRawJson}
            >
              <Code className="h-4 w-4 mr-2" />
              View Raw JSON
            </Button>
          )}
          <Link to={`/admin/blockchain/transactions?artifactId=${artifactId}`}>
            <Button variant="outline" size="sm">
              <ExternalLink className="h-4 w-4 mr-2" />
              View All for Artifact
            </Button>
          </Link>
        </div>
      </div>

      {/* Transaction Details */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="space-y-0.5">
          <label className="text-xs font-medium text-muted-foreground">Transaction ID</label>
          <div className="flex items-center space-x-1">
            <p className="font-mono text-sm p-1.5 bg-muted rounded break-all flex-1">
              {transactionId}
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => copyToClipboard(transactionId, 'Transaction ID')}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="space-y-0.5">
          <label className="text-xs font-medium text-muted-foreground">Artifact ID</label>
          <div className="flex items-center space-x-1">
            <p className="font-mono text-sm p-1.5 bg-muted rounded break-all flex-1">
              {artifactId}
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => copyToClipboard(artifactId, 'Artifact ID')}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="space-y-0.5">
          <label className="text-xs font-medium text-muted-foreground">Created Date</label>
          <div className="p-1.5 bg-muted rounded">
            <p className="text-sm font-medium">
              {format(new Date(insertedAt), 'MMM d, yyyy HH:mm:ss')}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
